import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { dateIt, eur } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ClientEnrollmentStatus from "@/components/app/ClientEnrollmentStatus";
import { UserPlus, Ticket, AlertTriangle, CalendarCheck, BookOpen, ClipboardList, Receipt } from "lucide-react";

function SlotWidget({ d }) {
  const zero = d.slots_balance <= 0;
  const low = !zero && d.slots_balance <= d.slots_low_threshold;
  const tone = zero ? "border-red-200 bg-red-50 text-red-700" : low ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700";
  return (
    <Card className={`border ${tone}`} data-testid="slots-widget">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <Ticket className="w-6 h-6" />
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Slot disponibili</div>
        </div>
        <div className="font-display font-extrabold text-5xl mt-2" data-testid="slots-balance">{d.slots_balance}</div>
        {(zero || low) && (
          <div className="mt-3 flex items-start gap-2 text-sm font-medium" data-testid="slots-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {zero ? "Slot esauriti: non è possibile effettuare nuove iscrizioni. Contatta l'amministratore per una nuova ricarica."
              : "Slot in esaurimento: contatta l'amministratore per una nuova ricarica."}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="border-slate-200"><CardContent className="p-5">
      <div className="flex items-center justify-between"><div className="text-xs uppercase tracking-widest font-bold text-amber-600">{label}</div><Icon className="w-5 h-5 text-slate-400" /></div>
      <div className="font-display font-extrabold text-3xl mt-3 text-slate-900" data-testid={testid}>{value}</div>
    </CardContent></Card>
  );
}

const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

function Line({ label, value, strong, testid }) {
  return (
    <div className={`flex justify-between text-sm ${strong ? "font-bold text-slate-900 pt-2 border-t mt-2" : "text-slate-600"}`}>
      <span>{label}</span><span className="font-mono" data-testid={testid}>{value}</span>
    </div>
  );
}

function MonthlyBilling() {
  const [month, setMonth] = useState("");
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setError(false);
    api.get("/cliente/billing-summary", { params: month ? { month } : {} }).then((r) => setB(r.data)).catch(() => setError(true));
  }, [month]);
  if (error && !b) return <Card className="border-red-200"><CardContent className="p-5 text-sm text-red-700" data-testid="billing-error">Impossibile caricare il riepilogo della fatturazione. Riprova più tardi.</CardContent></Card>;
  if (!b) return <Card className="border-indigo-200"><CardContent className="p-5 text-sm text-slate-500">Caricamento fatturazione…</CardContent></Card>;
  const n = (c) => `${c} ${c === 1 ? "iscrizione" : "iscrizioni"}`;
  return (
    <Card className="border-indigo-200" data-testid="monthly-billing-card"><CardContent className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3"><Receipt className="w-6 h-6 text-indigo-700" />
          <h2 className="font-display text-xl font-bold">Fatturazione del mese</h2></div>
        <Select value={b.month} onValueChange={setMonth}>
          <SelectTrigger className="w-48 capitalize" data-testid="billing-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{b.months.map((m) => <SelectItem key={m} value={m} className="capitalize">{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-4" data-testid="billing-with-vat">
          <div className="text-xs uppercase tracking-widest font-bold text-amber-600 mb-2">Corsi con IVA · {n(b.with_vat.count)}</div>
          <Line label="Imponibile" value={eur(b.with_vat.net)} testid="billing-vat-net" />
          <Line label="IVA" value={eur(b.with_vat.vat)} testid="billing-vat-vat" />
          <Line label="Totale imponibile + IVA" value={eur(b.with_vat.total)} strong testid="billing-vat-total" />
        </div>
        <div className="rounded-xl border border-slate-200 p-4" data-testid="billing-vat-exempt">
          <div className="text-xs uppercase tracking-widest font-bold text-amber-600 mb-2">Corsi esenti IVA · {n(b.vat_exempt.count)}</div>
          <Line label="IVA" value="Esente" />
          <Line label="Totale solo imponibile" value={eur(b.vat_exempt.net)} strong testid="billing-exempt-net" />
        </div>
      </div>
      <div className="flex justify-between items-center rounded-xl bg-indigo-50 text-indigo-900 px-4 py-3">
        <span className="font-semibold">Totale da fatturare · <span className="capitalize">{monthLabel(b.month)}</span></span>
        <span className="font-display font-extrabold text-2xl" data-testid="billing-month-total">{eur(b.total)}</span>
      </div>
      <p className="text-xs text-slate-500">Calcolato sulle iscrizioni confermate del mese; le iscrizioni annullate sono escluse.</p>
    </CardContent></Card>
  );
}

export default function ClienteDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/cliente/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Caricamento…</div>;
  const blocked = d.billing_mode === "slot" && d.slots_balance <= 0;
  return (
    <div className="space-y-6" data-testid="cliente-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Benvenuto, {d.company_name}</h1>
          <p className="text-slate-500 mt-1">{d.billing_mode === "slot" ? "Modalità Slot prepagati" : "Modalità Fatturazione mensile"}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-0 brand-gradient text-white">
          <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 h-full">
            <div>
              <div className="font-display text-2xl font-extrabold">Nuova iscrizione</div>
              <div className="text-slate-200 text-sm mt-1">Iscrivi un corsista ai corsi del tuo catalogo dedicato.</div>
            </div>
            {blocked ? (
              <Button disabled className="h-14 px-8 text-lg bg-white/30 text-white" data-testid="dashboard-iscrizioni-button">ISCRIZIONI</Button>
            ) : (
              <Button asChild className="h-14 px-8 text-lg font-bold bg-amber-500 hover:bg-amber-600 text-white" data-testid="dashboard-iscrizioni-button">
                <Link to="/cliente/iscrizioni"><UserPlus className="w-5 h-5 mr-2" /> ISCRIZIONI</Link>
              </Button>
            )}
          </CardContent>
        </Card>
        {d.billing_mode === "slot" ? <SlotWidget d={d} /> : (
          <Card className="border-indigo-200 bg-indigo-50" data-testid="billing-widget"><CardContent className="p-5 text-indigo-800">
            <div className="flex items-center gap-3"><Receipt className="w-6 h-6" /><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Fatturazione mensile</div></div>
            <div className="text-sm mt-3">Le iscrizioni vengono registrate e contabilizzate nella fattura del mese.</div>
          </CardContent></Card>
        )}
      </div>
      {d.billing_mode === "mensile" && <MonthlyBilling />}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={ClipboardList} label="Iscrizioni totali" value={d.enrollments_total} testid="stat-enrollments-total" />
        <Stat icon={CalendarCheck} label="Iscrizioni del mese" value={d.enrollments_month} testid="stat-enrollments-month" />
        <Stat icon={BookOpen} label="Corsi a catalogo attivi" value={d.courses_available} testid="stat-courses" />
      </div>
      <Card className="border-slate-200"><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold">Ultime iscrizioni</h2>
          <Link to="/cliente/storico" className="text-sm font-semibold text-amber-600" data-testid="dashboard-view-all">Vedi tutte →</Link>
        </div>
        {d.recent.length === 0 && <div className="text-slate-500 text-sm">Nessuna iscrizione effettuata.</div>}
        <div className="divide-y">
          {d.recent.map((e) => (
            <div key={e.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{e.first_name} {e.last_name} · {e.course_title}</div>
                <div className="text-xs text-slate-500">{e.enrollment_number} · {dateIt(e.created_at)}</div>
              </div>
              <ClientEnrollmentStatus status={e.status} />
            </div>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
}
